/**
 * Shared display formatters for the admin.
 *
 * House rules (enforced across every admin screen):
 *   - numeric quantities render with EXACTLY two decimals, never more, never less (30 -> "30.00");
 *   - money carries the руб sign ("30.00 ₽"), per-litre prices "30.00 ₽/л";
 *   - volumes carry the litre unit ("30.00 л");
 *   - fuel is shown by its Russian name (АИ-95), never its code (AI_95).
 *
 * Everything routes through {@link formatDecimal2}. It always emits a dot decimal separator
 * regardless of the browser locale, matching the Android Locale.US formatter and the dotted wire
 * format. Do NOT swap in Intl.NumberFormat — a locale comma would diverge from the operator app
 * and the стоимость calc. A null / empty / non-numeric value renders as an em dash.
 *
 * Rounding is done on the DECIMAL DIGITS directly (round-half-up), not via Number.prototype.toFixed:
 * toFixed is IEEE754-float-precision-lossy at exactly-half boundaries — famously
 * `(1.005).toFixed(2) === '1.00'`, not `'1.01'`, because 1.005 can't be represented exactly in
 * binary floating point. That boundary is not hypothetical here: `fuelAmount / 1000` (ml → l,
 * CLAUDE.md gotcha #4) routinely produces exact 3-decimal values like 26.615.
 *
 * The only thing NOT covered by the two-decimals rule (it carries its own precision): geographic
 * coordinates (lat/lon). Free-text numeric INPUT fields (editable price/rate/liters/capacity) ARE
 * covered too — see {@link toEditableDecimal2} — the wire value just can't carry a unit suffix or
 * an em dash the way a read-only display can.
 */

export const EM_DASH = '—';

/** Exactly two decimals, dot separator, no thousands grouping. `—` for null / empty / non-numeric. */
export function formatDecimal2(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return EM_DASH;
  if (Number.isNaN(Number(value))) return EM_DASH;
  const raw = typeof value === 'number' ? value.toString() : value;
  return roundHalfUpToTwoDecimals(raw);
}

/**
 * Round-half-up to 2 decimal places, working on the digit string itself so binary floating-point
 * representation never enters the picture (see the module doc comment for why `toFixed` can't be
 * trusted here). Only ever called with a value that already passed `Number.isNaN` above, so it can
 * assume a plain `[-]digits[.digits]` shape — no scientific notation, which decimal wire values
 * (Rust's `rust_decimal::Decimal` serialized to a string) never produce.
 */
function roundHalfUpToTwoDecimals(raw: string): string {
  const trimmed = raw.trim();
  const negative = trimmed.startsWith('-');
  const unsigned = trimmed.replace(/^[+-]/, '');
  const [intPartRaw, fracPartRaw = ''] = unsigned.split('.');
  const intPart = intPartRaw === '' ? '0' : intPartRaw;

  if (fracPartRaw.length <= 2) {
    return `${negative ? '-' : ''}${intPart}.${fracPartRaw.padEnd(2, '0')}`;
  }

  const kept = fracPartRaw.slice(0, 2);
  const roundUp = fracPartRaw.charCodeAt(2) >= 53; // '5'.charCodeAt(0) === 53
  const cents = BigInt(intPart + kept) + (roundUp ? 1n : 0n);
  const centsStr = cents.toString().padStart(3, '0');
  const whole = centsStr.slice(0, -2);
  const cent = centsStr.slice(-2);
  return `${negative && cents !== 0n ? '-' : ''}${whole}.${cent}`;
}

/**
 * Litres display: exactly two decimals, never collapsed to a whole number (30 -> "30.00"). Bare
 * number without a unit — use {@link formatLitersUnit} when the value stands alone and needs "л".
 */
export function formatLiters(value: string | number | null | undefined): string {
  return formatDecimal2(value);
}

/** Volume with unit: "30.00 л" (or `—`). */
export function formatLitersUnit(value: string | number | null | undefined): string {
  const s = formatDecimal2(value);
  return s === EM_DASH ? s : `${s} л`;
}

/** Russian plural for a count: «1 вершина», «2 вершины», «48 вершин».
 *
 * Russian picks the form by the last two digits, and the exceptions (11-14 take the many-form
 * despite ending in 1-4) are exactly what a naive `n === 1 ? a : b` gets wrong. Takes the three
 * forms in the order [одна, две, пять]. */
export function pluralRu(count: number, forms: [string, string, string]): string {
  const n = Math.abs(Math.trunc(count));
  const tens = n % 100;
  if (tens >= 11 && tens <= 14) return forms[2];
  const ones = n % 10;
  if (ones === 1) return forms[0];
  if (ones >= 2 && ones <= 4) return forms[1];
  return forms[2];
}

/** Money: "30.00 ₽" (or `—`). Two decimals + руб sign. */
export function formatMoney(value: string | number | null | undefined): string {
  const s = formatDecimal2(value);
  return s === EM_DASH ? s : `${s} ₽`;
}

/** Per-litre price: "59.00 ₽/л" (or `—`). */
export function formatPricePerLiter(value: string | number | null | undefined): string {
  const s = formatDecimal2(value);
  return s === EM_DASH ? s : `${s} ₽/л`;
}

/**
 * Same two-decimal rounding as {@link formatDecimal2}, but for an EDITABLE numeric `<Input>`
 * value rather than read-only text: no unit suffix (the field must submit back as a plain
 * number), and empty/null/undefined resolves to `''` (a blank field to type into), not the em
 * dash — an em dash isn't something an admin can edit over. A genuinely non-numeric value is
 * passed through unchanged rather than blanked, so a bad wire value stays visible/fixable
 * instead of silently disappearing.
 */
export function toEditableDecimal2(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '';
  if (Number.isNaN(Number(value))) return String(value);
  const raw = typeof value === 'number' ? value.toString() : value;
  return roundHalfUpToTwoDecimals(raw);
}

/**
 * «500.0000» → «500», «1200.5000» → «1200.5», «0.00» → «0».
 *
 * Postgres hands decimals over at full scale, which is right on the wire and unreadable in a form:
 * an admin fixing a delivery price should see 500, not four zeros they have to select past. Only
 * the trailing fraction goes — a significant digit never does, so a per-litre 4.5 stays 4.5.
 */
export function trimZeros(value: string): string {
  if (!value.includes('.')) return value;
  const trimmed = value.replace(/\.?0+$/, '');
  // «», «-» and «-0» all mean nothing — a price field showing «-0» reads as a broken screen.
  return trimmed === '' || trimmed === '-' || trimmed === '-0' ? '0' : trimmed;
}

/** Local date+time via ru-RU locale (e.g. "28.06.2026, 13:00:00"). `—` for null / empty / invalid. */
export function formatDateTime(value: string | null | undefined): string {
  if (!value) return EM_DASH;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return EM_DASH;
  return d.toLocaleString('ru-RU');
}

/**
 * The booked delivery window as a dispatcher reads it: «19 августа, 17:00–21:00», or
 * «19 августа, 23:00 – 20 августа, 03:00» for the rare window that crosses midnight. Local time —
 * the same wall clock the operator will drive by.
 */
export function formatDeliveryWindow(from: Date, to: Date): string {
  const day = new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long' });
  const time = new Intl.DateTimeFormat('ru-RU', { hour: '2-digit', minute: '2-digit' });
  if (day.format(from) === day.format(to)) {
    return `${day.format(from)}, ${time.format(from)}\u2013${time.format(to)}`;
  }
  return `${day.format(from)}, ${time.format(from)} \u2013 ${day.format(to)}, ${time.format(to)}`;
}

/** «Лада Ларгус» из двух необязательных половин. Заведена одна — показываем одну; ни одной —
    пустая строка, а не строка из пробелов, которую таблица нарисует как заполненную ячейку.
    Марка бензовоза появилась поздно, и у половины парка её нет — это норма, не пробел в данных. */
export function truckMake(car: {
  brand?: string | null;
  model?: string | null;
}): string {
  return [car.brand, car.model]
    .map((part) => part?.trim() ?? '')
    .filter((part) => part !== '')
    .join(' ');
}
